import { useState, useEffect, useMemo, useCallback, useRef, useImperativeHandle, RefObject } from "react";
import { useMutation, useQuery } from "react-query";
import { useTranslation, Trans } from "react-i18next";
import i18n from "i18next";
import cn from "classnames";
import NumberInput from "@components/new/NumberInput";
import { Marketplace } from "@components/new/Marketplace";
import SearchInput from "@components/new/SearchInput";
import Photo from "@components/new/Photo";
import { Title2, SubTitle3 } from "@components/new/Typo";
import { Accordeon } from "@components/new/Accordeon";
import { Button, ButtonSize, ButtonType } from "@components/new/Button";
import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import ItemSearchItem from "../../components/ItemSearchItem";
import { Alert, AlertType } from "@components/new/Alert";
import { Table, TableCellLink, TableColumn } from "@components/new/Table";
import { TabsList, TabsContent } from "@components/new/Tabs/Tabs";
import { MessageModalContent } from '@components/modals/MessageModalNew';
import { Tooltip } from '@components/new/Tooltip';
import {
  createFBPRestockRequest,
  // updateWbProducts,
  validateFBPRestockExcel,
  isWbRequestItem,
  isYmRequestItem,
  isAliRequestItem,
  isUnknownRequestItem,
  searchItems,
  getFBPPickupTariff,
  TYmItemSearch,
  TWbItemSearch,
  TAliItemSearch,
  TValidationRequestItem as TRequestItem,
  TProductDimensionLimits,
} from "@api/apiClient/fbp";
import { SearchMenuComponent } from "./SearchMenuComponent";
import { useProfile } from "@contexts/ProfileContext";
import { weightKgValue, formatSumWithCurrency } from "@utils/formatValues";
import { getDimensionsErrorHint } from "@utils/fbpDimensions";
import { FormHandle } from "./types";
import emptyImg from '@assets/img/empty.png';
import DelIcon from '@assets/icons/outline/trash-2.svg?react';
import FileDownIcon from '@assets/icons/outline/file-down.svg?react';
import FilePlusIcon from '@assets/icons/outline/file-plus.svg?react';
import InfoIcon from "@assets/icons/outline/info.svg?react";
import SolidInfoIcon from "@assets/icons/solid/circle-info.svg?react";
// import PackageOpenIcon from "@assets/icons/outline/package-open.svg?react";
import NoItemsIcon from "@assets/icons/hugeicons/package-remove.svg?react";
import styles from './Form.module.scss';

type TWbItemWithCount = TWbItemSearch & { count?: number }
type TYmItemWithCount = TYmItemSearch & { count?: number }
type TAliItemWithCount = TAliItemSearch & { count?: number }

export interface FormType {
  wbId: string;
  wbSellerId: string;
  ymSellerId: string;
}

type TRow = (TYmItemSearch | TWbItemSearch | TAliItemSearch) & {
  count?: number,
};

const Form = ({
  formRef,
  onStatusChange,
  onSuccess,
  onError,
  sellerWarehouseId,
  fbpWarehouseId,
  isPickUpAvailable,
  setAccordeonPage,
  wbDimensionLimits,
  aliDimensionLimits,
  ymDimensionLimits,
  wbDensityLimits,
  aliDensityLimits,
  ymDensityLimits,
}: {
  formRef?: RefObject<FormHandle>;
  onStatusChange: (props: {
    disabled: boolean,
    isLoading?: boolean,
    pickUpAvailable?: boolean;
  }, msg: React.ReactNode) => void;
  onSuccess: (id: string) => void;
  onError: () => void;
  sellerWarehouseId: string;
  fbpWarehouseId: string;
  isPickUpAvailable: boolean;
  setAccordeonPage: (v: string) => void;
  wbDimensionLimits?: TProductDimensionLimits;
  aliDimensionLimits?: TProductDimensionLimits;
  ymDimensionLimits?: TProductDimensionLimits;
  wbDensityLimits?: number;
  aliDensityLimits?: number;
  ymDensityLimits?: number;
}) => {
  const currentLanguage = i18n.language;
  const { t } = useTranslation();
  const { account } = useProfile();
  // Feature FreeFbpPickUp: пикап бесплатный — не показываем стоимость тарифа.
  const isFreeFbpPickUp = Boolean(account?.features?.freeFbpPickUp);
  const [boxCount, setBoxCount] = useState("1");
  const [maxBoxCount, setMaxBoxCount] = useState(1);
  const [items, setItems] = useState<TRequestItem[]>([]);
  const [invalidItems, setInvalidItems] = useState<TRequestItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getIsValid = () => {
    return (items?.length > 0) &&
      items?.length <= 1000 &&
      !items.find(v => !v.count) &&
      !invalidItems.find(v => !v.count) &&
      Boolean(boxCount);
  }

  const {
    isLoading,
    mutate,
  } = useMutation({
    mutationFn: createFBPRestockRequest,
    onSuccess: (res) => onSuccess(res.id),
    onError: () => onError(),
  });

  const validateExcelMutation = useMutation({
    mutationFn: validateFBPRestockExcel,
    onSuccess: (data) => {
      setItems(data.validItems);
      setInvalidItems(data.invalidItems);
    },
    // onError: () => onError(),
  });

  const { data: tariff } = useQuery({
    queryKey: ['getFBPPickupTariff', fbpWarehouseId],
    queryFn: () => getFBPPickupTariff(fbpWarehouseId),
    retry: 1,
    enabled: Boolean(fbpWarehouseId) && isPickUpAvailable && !isFreeFbpPickUp,
  });

  // Стабильный компонент меню: инлайн-обёртка пересоздавала бы его на каждом рендере
  const SearchMenu = useMemo(() => {
    const Menu = (props: React.ComponentProps<typeof SearchMenuComponent>) => (
      <SearchMenuComponent
        {...props}
        wbDimensionLimits={wbDimensionLimits}
        aliDimensionLimits={aliDimensionLimits}
        ymDimensionLimits={ymDimensionLimits}
        wbDensityLimits={wbDensityLimits}
        aliDensityLimits={aliDensityLimits}
        ymDensityLimits={ymDensityLimits}
      />
    );
    return Menu;
  }, [wbDimensionLimits, aliDimensionLimits, ymDimensionLimits]);
  const pricePerKg = tariff?.pricePerKg ?? 0;
  const currency = tariff?.currency ?? "CNY";

  const totalWeight = useMemo(() => (
    items.reduce((acc, v) => {
      if (isUnknownRequestItem(v)) return acc;
      return acc + weightKgValue(v.weight) * (v.count ?? 0);
    }, 0)
  ), [items]);
  const weightPasses = totalWeight >= 10 && totalWeight <= 3000;

  useEffect(() => {
    onStatusChange({
      disabled: !getIsValid(),
      isLoading: Boolean(isLoading),
      pickUpAvailable: weightPasses && isPickUpAvailable,
    }, items?.length > 1000 ? (
      <Alert
        type={AlertType.ERROR}
        title={t("fbpPage.createOrder.itemsCountLimit")}
      />
    ) : (invalidItems.find(v => isUnknownRequestItem(v)) ? (
      <Alert
        type={AlertType.WARNING}
        title={t("fbpPage.createOrder.hasUnknownItems")}
      />
    ) : (invalidItems.length ? (
      <Alert
        type={AlertType.WARNING}
        title={t("fbpPage.createOrder.hasInvalidItems")}
      />
    ) : null)));
  }, [
    boxCount,
    isLoading,
    items,
    invalidItems,
    currentLanguage,
    weightPasses,
    isPickUpAvailable,
  ])

  useEffect(() => {
    const newLimit = items.reduce((acc, v) => ((v.count ?? 0) + acc), 0) || 1;
    setMaxBoxCount(newLimit);
    if (+boxCount > newLimit) setBoxCount(String(newLimit));
  }, [items])

  const onSubmit = () => {
    mutate({
      sellerWarehouseId,
      boxCount: +boxCount || 0,
      ymProducts: (items.filter(v => isYmRequestItem(v)) as TYmItemWithCount[]).map(v => ({
        ymProductId: v.id,
        count: v.count ?? 0,
      })),
      wbProducts: (items.filter(v => isWbRequestItem(v)) as TWbItemWithCount[]).map(v => ({
        wbProductId: v.id,
        count: v.count ?? 0,
      })),
      aliProducts: (items.filter(v => isAliRequestItem(v)) as TAliItemWithCount[]).map(v => ({
        aliProductId: v.id,
        count: v.count ?? 0,
      })),
    });
  };

  useImperativeHandle(formRef, () => ({
    submit: onSubmit,
  }));

  const handleRemoveItem = useCallback((id: string) => {
    setItems(items.filter(item => (id !== item.id)))
  }, [items])
  const handleUpdateCount = useCallback((v: number, id: string) => {
    setItems((oldItems) => {
      const newItems = oldItems.map((item) => (
        (id === item.id) ? ({
          ...item,
          count: v,
        }) : item
      ));
      return newItems;
    });
  }, [items, setItems])

  const handleUpdateInvalidCount = useCallback((v: number, index: number) => {
    const newItems = invalidItems.map((item, idx) => (
      idx === index ? ({
        ...item,
        count: v,
      }) : item
    ));
    setInvalidItems(newItems);
  // }, [invalidItems])
  }, [invalidItems])

  const handleSelection = (selected: TRequestItem[]) => {
    setItems([
      ...items,
      ...selected.filter(item => !items.find(
        v => ((v.id === item.id) && (item.marketplace === v.marketplace))
      )).map(v => ({ ...v, count: v.count || 0 })),
    ]);
  }

  const columns: TableColumn<TRequestItem>[] = useMemo(() => [
    {
      header: t("fbpPage.createOrder.items.photo"),
      renderCell: (row) => (
        isUnknownRequestItem(row.data) ? (
          <img src={emptyImg} alt="" className={styles.emptyImage} />
        ) : (
          <Photo src={row.data.primaryImage} />
        )
      ),
      width: "80px",
      fixWidth: true,
    },
    {
      header: t("fbpPage.createOrder.items.marketplace"),
      renderCell: (row) => (
        <Marketplace name={row.data.marketplace} size="small" />
      ),
      alignment: "center",
      width: "40px",
      fixWidth: true,
    },
    {
      header: t("fbpPage.createOrder.items.name"),
      renderCell: (row) => {
        if (isUnknownRequestItem(row.data)) {
          return (
            <ItemSearchItem
              sellerWarehouseId={sellerWarehouseId}
              data={row.data}
              onSuccess={
                (selected: TRequestItem) => {
                  handleSelection([selected]);
                  setInvalidItems(invalidItems.filter((_, idx) => (idx !== row.index)))
                }
              }
            />
          )
        }
        const description = isWbRequestItem(row.data) ? row.data.wbNmId : (
          isAliRequestItem(row.data) ? row.data.skuCode : row.data.sku);
        const recommendedDensity = (isWbRequestItem(row.data) ? wbDensityLimits : (
          isAliRequestItem(row.data) ? aliDensityLimits : ymDensityLimits)) || 0;
        const density = Math.round((row.data.density?.value ?? 0) * 100) / 100;
        return (
          <TableCellLink
            title={row.data.name}
            description={description}
            thirdLine={
              (density && recommendedDensity && (density < recommendedDensity)) ? (
                <SubTitle3 className={styles.lowDensityWarning}>
                  {t("fbpPage.createOrder.lowDensity")}
                  <Tooltip
                    action={
                      <IconButton
                        icon={SolidInfoIcon}
                        onClick={() => { }}
                        size={IconButtonSize.L}
                        type={IconButtonType.ICON}
                      />
                    }
                    position="top"
                    text={t(`fbpPage.createOrder.lowDensityTooltip`, { density, recommendedDensity })}
                    whiteSpace="normal"
                    maxWidth={320}
                  />
                </SubTitle3>
              ) : null}
            to={row.data.url}
            external
            maxWidth="460px"
          />
        )
      },
      width: "200px",
    },
    {
      header: t("fbpPage.createOrder.items.size"),
      valueGetter: (row) => ((isWbRequestItem(row) && row.wbTechSize) || ""),
      width: "50px",
      fixWidth: true,
      alignment: "center",
    },
  ], [currentLanguage, items]);

  const goodItemsColumns: TableColumn<TRequestItem>[] = useMemo(() => [
    ...columns,
    {
      header: t("fbpPage.createOrder.items.count"),
      renderCell: (row) => (
        <NumberInput
          value={row.data.count ?? 0}
          onChange={(v) => handleUpdateCount(v, row.data.id)}
          min={0}
          max={99999999}
          hasError={!row.data.count}
          isWide
        />
      ),
      width: "180px",
      fixWidth: true,
    },
    {
      header: (<IconButton
        icon={DelIcon}
        onClick={() => setItems([])}
        size={IconButtonSize.L}
        type={IconButtonType.ICON}
      />),
      renderCell: (row) => (
        <IconButton
          icon={DelIcon}
          onClick={() => handleRemoveItem(row.data.id)}
          size={IconButtonSize.L}
          type={IconButtonType.ICON}
        />
      ),
      // hug: shrink the column to the delete button width
      width: "1px",
      fixWidth: true,
    },
  ], [columns, items, handleUpdateCount, handleRemoveItem]);

  const badItemsColumns: TableColumn<TRequestItem>[] = useMemo(() => [
    ...columns,
    {
      header: t("fbpPage.createOrder.items.count"),
      renderCell: (row) => (
        isUnknownRequestItem(row.data) ? (
          <NumberInput
            value={row.data.count ?? 0}
            onChange={(v) => handleUpdateInvalidCount(v, row.index)}
            min={0}
            hasError={!row.data.count}
          />
        ) : (
          // Количество из Excel — товар в заявку не попадёт, поэтому без редактирования
          <span>{row.data.count ?? 0}</span>
        )
      ),
      width: "160px",
      fixWidth: true,
      alignment: "center",
    },
    {
      header: "",
      renderCell: (row) => (
        isUnknownRequestItem(row.data) ? null : (
          <Tooltip
            action={
              <IconButton
                icon={InfoIcon}
                onClick={() => {}}
                size={IconButtonSize.L}
                type={IconButtonType.ICON}
              />
            }
            position="top"
            text={
              getDimensionsErrorHint(row.data, wbDimensionLimits, aliDimensionLimits, ymDimensionLimits, t) ||
              t(`fbpPage.createOrder.errorTypes.${row.data.errorType ?? "Suspicious"}`, {
                marketplace: t(`marketplace.${row.data.marketplace}`),
              })
            }
            whiteSpace="normal"
            maxWidth={320}
          />
        )
      ),
      width: "60px",
      fixWidth: true,
    },
  ], [columns, invalidItems, handleUpdateInvalidCount, wbDimensionLimits, aliDimensionLimits, ymDimensionLimits]);

  const handleXlsFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = (event.target.files ? Array.from(event.target.files) : [])[0];
    validateExcelMutation.mutate({ sellerWarehouseId, file });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const tabs = [
    {
      id: "valid-items",
      title: t('fbpPage.createOrder.avaliableItems'),
      content: (
        items.length ? (
          <Table<TRequestItem>
            key={JSON.stringify(items.map(v => v.id))}
            columns={goodItemsColumns}
            data={items ?? []}
          />
        ) : (
          <div className={styles.noItemsContainer}>
            <MessageModalContent
              title={t('fbpPage.createOrder.items.noItemsTitle')}
              description={t('fbpPage.createOrder.items.noItemsDescription')}
              variant="info"
              Icon={<NoItemsIcon />}
              iconSize="l"
            />
          </div>
        )
      ),
      count: items.length,
    },
    {
      id: "invalid-items",
      title: t('fbpPage.createOrder.unavaliableItems'),
      content: (
        <Table<TRequestItem>
          key={JSON.stringify(invalidItems)}
          columns={badItemsColumns}
          data={invalidItems ?? []}
          rowId={(data: TRequestItem) => {
            if (isWbRequestItem(data)) return `${data.marketplace}-${data.wbNmId}`
            if (isAliRequestItem(data)) return `${data.marketplace}-${data.skuCode}`
            return `${data.marketplace}-${data.sku}`
          }}
        />
      ),
      count: invalidItems.length,
    },
  ]

  const [ activeTab, setActiveTab ] = useState("valid-items");

  const views = [
    {
      title: t('fbpPage.createOrder.items.title'),
      id: "items",
      content: (
        <div className={cn(styles.section, styles.sectionItems)}>
          <div className={styles.descriptionRow}>
            {/* По макету подсказка про артикулы видна постоянно, названия полей — жирным */}
            <SubTitle3 color="grey-700">
              <Trans i18nKey="fbpPage.createOrder.items.descriptionTooltip" />
            </SubTitle3>
          </div>

          <div className={styles.inputRow}>
            <SearchInput<TRow>
              className={styles.searchInput}
              getItems={(search?: string) => searchItems({
                sellerWarehouseId: sellerWarehouseId,
                search: search ?? "",
              })}
              placeholder={t("fbpPage.createOrder.items.searchInput")}
              requestKey={`searchItems-${sellerWarehouseId}`}
              keyExtractor={(item: TRow) => item.id}
              MenuComponent={SearchMenu}
              minLength={0}
              onItemSelect={handleSelection}
            />
            <input
              ref={fileInputRef}
              className={styles.fileInput}
              type="file"
              onChange={handleXlsFile}
              accept=".xls, .xlsx"
            />
            <Button
              icon={FilePlusIcon}
              size={ButtonSize.M}
              type={ButtonType.SECONDARY}
              onClick={() => {
                fileInputRef?.current?.click?.()
              }}
              title={t("fbpPage.createOrder.uploadFileBtn")}
              loading={validateExcelMutation.isLoading}
            />
            <LinkButton
              icon={FileDownIcon}
              size={LinkButtonSize.L}
              title={t("fbpPage.createOrder.templeteLink")}
              external
              to="/docs/UNI_template_FBP_inbound.xlsx"
            />
          </div>

          {invalidItems.length ? (
            <>
              <TabsList
                tabs={tabs}
                onChange={setActiveTab}
                value={activeTab}
              />

              <TabsContent tab={tabs.find((tab) => (tab.id === activeTab))} className={styles.tabConent}/>
            </>
          ) : (
            tabs[0].content
          )}
        </div>
      ),
    },
    {
      title: t('fbpPage.createOrder.boxCount.title'),
      id: "spaces",
      content: (
        <div className={styles.section}>
          <Alert
            type={AlertType.WARNING}
            title={t("fbpPage.createOrder.boxCount.alert")}
            description={t("fbpPage.createOrder.boxCount.description")}
          />
          <div className={styles.boxCountInputContainer}>
            <Title2 color="grey-800">{t("fbpPage.createOrder.boxCount.input")}</Title2>
            {`${boxCount}/${maxBoxCount}`}
            <NumberInput
              key={`${boxCount}-${maxBoxCount}`}
              // className={styles.boxCountInput}
              value={+boxCount}
              onChange={v => {
                setBoxCount(String(v))
              }}
              min={0}
              max={maxBoxCount}
              hasError={!boxCount}
            />
          </div>
        </div>
      ),
    }
  ]

  return (
    <div className={styles.form}>
      {isPickUpAvailable && items.length > 0 ? (
        weightPasses ? (
          <Alert
            type={AlertType.NEUTRAL}
            title={isFreeFbpPickUp
              ? t("fbpPage.pickUp.alertTitleFree")
              : t("fbpPage.pickUp.alertTitle", { price: formatSumWithCurrency(pricePerKg, currency) })}
            description={t("fbpPage.pickUp.alertDescription")}
          />
        ) : (
          <Alert
            type={AlertType.WARNING}
            title={t("fbpPage.createOrder.pickupWeightTitle")}
            description={t("fbpPage.createOrder.pickupWeightDescription")}
          />
        )
      ) : null}
      <Accordeon
        defaultOpenId="items"
        items={views}
        stretch
        className={styles.accordeon}
        summaryClassName={styles.accordeonSummary}
        onChange={setAccordeonPage}
      />
    </div>
  )
};

export default Form;
